/**
 * Kora pflegen – der fünfte Reiter im Admin Center.
 *
 * Vier Unterbereiche: Sätze, Tonlage, Schwellwerte, Portraits. Was hier
 * geändert wird, gilt ab der nächsten Anmeldung; laufende Sitzungen behalten
 * ihre Tonlage und ihren Satz.
 *
 * Gespeichert wird sofort beim Verlassen eines Feldes – so wie in den anderen
 * Reitern dieses Dialogs auch. Die Anleitung sah ein Sammeln bis zum
 * „Speichern & fertig" vor; das wäre in einem Fenster, das sonst überall
 * unmittelbar speichert, ein zweites Verhalten für dieselbe Handlung.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiFehler } from '../api';
import { useKora } from '../zustand';
import { dat, norm } from '../hilfen/format';
import { KoraPille } from '../komponenten/KoraKommentar';
import type { KoraSatz, KoraVerwaltungStand, KoraVerwaltungRegel } from '../typen';
import './koraverwaltung.css';

type Bereich = 'saetze' | 'tonlage' | 'schwellen' | 'portraits';

export function KoraVerwaltung() {
  const { toastZeigen } = useKora();
  const queryClient = useQueryClient();
  const [bereich, setBereich] = useState<Bereich>('saetze');

  const abfrage = useQuery({
    queryKey: ['kora-verwaltung'],
    queryFn: () => api.hole<KoraVerwaltungStand>('/kora/verwaltung'),
  });

  function neuLaden() {
    void queryClient.invalidateQueries({ queryKey: ['kora-verwaltung'] });
    void queryClient.invalidateQueries({ queryKey: ['kora-satz'] });
  }

  const d = abfrage.data;

  const bereiche: { key: Bereich; label: string }[] = [
    { key: 'saetze', label: 'Sätze' },
    { key: 'tonlage', label: 'Tonlage' },
    { key: 'schwellen', label: 'Schwellwerte' },
    { key: 'portraits', label: 'Portraits' },
  ];

  return (
    <div className="kv">
      <div className="kv-kopf">
        <div>
          <div className="ein-titel">Kora</div>
          {/*
            Steht bewusst hier oben: Wer einen Satz ändert und ihn gleich
            danach in der Maske sucht, würde ihn nicht finden und den Fehler
            bei sich vermuten.
          */}
          <div className="ein-hinweis">
            Änderungen gelten ab der nächsten Anmeldung. Laufende Sitzungen behalten ihren Stand.
          </div>
        </div>
      </div>

      <div className="kv-bereiche">
        {bereiche.map((b) => (
          <button
            key={b.key}
            type="button"
            className={`kv-bereich ${bereich === b.key ? 'aktiv' : ''}`}
            onClick={() => setBereich(b.key)}
          >
            {b.label}
          </button>
        ))}
      </div>

      {abfrage.isLoading && <div className="ein-hinweis">Wird geladen …</div>}

      {d && bereich === 'saetze' && <Saetze stand={d} onGeaendert={neuLaden} />}
      {d && bereich === 'tonlage' && <Tonlage stand={d} onGeaendert={neuLaden} melden={toastZeigen} />}
      {d && bereich === 'schwellen' && (
        <Schwellen stand={d} onGeaendert={neuLaden} melden={toastZeigen} />
      )}
      {d && bereich === 'portraits' && (
        <Portraits stand={d} onGeaendert={neuLaden} melden={toastZeigen} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sätze
// ---------------------------------------------------------------------------

function Saetze({
  stand,
  onGeaendert,
}: {
  stand: KoraVerwaltungStand;
  onGeaendert: () => void;
}) {
  const [suche, setSuche] = useState('');
  const [offen, setOffen] = useState<string | null>(null);
  const q = norm(suche);

  const gefiltert = useMemo(
    () =>
      stand.regeln.filter(
        (r) =>
          !q ||
          [r.situation, r.normal, r.kora, r.trocken].some((t) => norm(t).includes(q)),
      ),
    [stand.regeln, q],
  );

  return (
    <>
      <input
        className="kv-suche"
        value={suche}
        onChange={(e) => setSuche(e.target.value)}
        placeholder="Situation oder Satz suchen"
        aria-label="Sätze durchsuchen"
      />

      <div className="kv-liste">
        {gefiltert.map((r) => (
          <Satzzeile
            key={r.id}
            regel={r}
            posen={stand.posen}
            offen={offen === r.id}
            onUmschalten={() => setOffen(offen === r.id ? null : r.id)}
            onGeaendert={onGeaendert}
          />
        ))}
        {gefiltert.length === 0 && <div className="ein-hinweis">Kein Satz gefunden.</div>}
      </div>
    </>
  );
}

function Satzzeile({
  regel,
  posen,
  offen,
  onUmschalten,
  onGeaendert,
}: {
  regel: KoraVerwaltungRegel;
  posen: string[];
  offen: boolean;
  onUmschalten: () => void;
  onGeaendert: () => void;
}) {
  const [fehler, setFehler] = useState('');

  const aendern = useMutation({
    mutationFn: (daten: Partial<KoraVerwaltungRegel>) =>
      api.aendere(`/kora/regel/${regel.id}`, daten),
    onSuccess: () => {
      setFehler('');
      onGeaendert();
    },
    onError: (f) => setFehler(f instanceof ApiFehler ? f.message : 'Speichern fehlgeschlagen.'),
  });

  function speichern(feld: 'normal' | 'kora' | 'trocken', wert: string) {
    if (wert === regel[feld]) return;
    aendern.mutate({ [feld]: wert } as Partial<KoraVerwaltungRegel>);
  }

  return (
    <div className={`kv-regel ${offen ? 'offen' : ''} ${regel.aktiv ? '' : 'stumm'}`}>
      <button type="button" className="kv-regelzeile" onClick={onUmschalten}>
        <span className="kv-prio">{regel.prio}</span>
        <span className="kv-situation">{regel.situation}</span>
        <span className="kv-bedingung">{regel.bedingungText}</span>
        <span className="kv-pose">{regel.pose}</span>
        <span className={`kv-status ${regel.aktiv ? 'aktiv' : ''}`}>
          {regel.aktiv ? 'aktiv' : 'stumm'}
        </span>
        <span className="kv-chevron" aria-hidden="true">
          {offen ? '⌃' : '⌄'}
        </span>
      </button>

      {offen && (
        <div className="kv-offen">
          {(['normal', 'kora', 'trocken'] as const).map((feld) => (
            <label className="kv-feld" key={feld}>
              <span className="kv-feldname">
                {feld === 'normal' ? 'Normal' : feld === 'kora' ? 'Kora' : 'Trocken'}
              </span>
              {/*
                Kursiv wie in der Anzeige: Wer hier tippt, soll sehen, wie es
                später dasteht – nicht erst in der Maske.
              */}
              <textarea
                className="kv-eingabe"
                defaultValue={regel[feld]}
                rows={2}
                onBlur={(e) => speichern(feld, e.target.value)}
              />
              <span className="kv-platzhalter">
                Erlaubt: {'{Vorname} {tage} {n} {summe} {Kollege} {warn} {krit}'}
                {' · '}
                {/* Einzahl und Mehrzahl in einem Satz – bezogen auf {n}. */}
                {'[Bestellung liegt|Bestellungen liegen]'} wählt nach {'{n}'}
              </span>
            </label>
          ))}

          <div className="kv-posenwahl">
            <span className="kv-feldname">Stimmung</span>
            <div className="kv-posen">
              {posen.map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`kv-posepille ${regel.pose === p ? 'aktiv' : ''}`}
                  onClick={() => regel.pose !== p && aendern.mutate({ pose: p })}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div className="kv-regelfuss">
            {/*
              Stumm statt löschen: Eine Regel verschwindet nie, sie schweigt
              nur. Sonst fehlte irgendwann die Bedingung im Programm zu einem
              Satz, den niemand mehr findet.
            */}
            <button
              type="button"
              className="kv-stumm"
              onClick={() => aendern.mutate({ aktiv: !regel.aktiv })}
            >
              {regel.aktiv ? 'Stummschalten' : 'Wieder aktivieren'}
            </button>
            <span className="kv-geaendert">
              {regel.geaendertVon
                ? `Zuletzt geändert ${dat(regel.geaendertAm.slice(0, 10))} · ${regel.geaendertVon}`
                : 'Noch nie geändert'}
            </span>
          </div>

          <div className="ein-hinweis">
            Bedingung und Priorität sind fest – sie stehen im Programm und hängen an der Kennung
            <code> {regel.id}</code>. Sichtbar in: {regel.maskenText || regel.masken.join(', ')}.
          </div>

          {fehler && <div className="eh-fehler">{fehler}</div>}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tonlage
// ---------------------------------------------------------------------------

function Tonlage({
  stand,
  onGeaendert,
  melden,
}: {
  stand: KoraVerwaltungStand;
  onGeaendert: () => void;
  melden: (t: string) => void;
}) {
  const e = stand.einstellung;
  const [kora, setKora] = useState(e.anteilKora);
  const [trocken, setTrocken] = useState(e.anteilTrocken);

  const speichern = useMutation({
    mutationFn: (daten: Record<string, number | boolean>) =>
      api.aendere('/kora/einstellung', daten),
    onSuccess: onGeaendert,
    onError: (f) => melden(f instanceof ApiFehler ? f.message : 'Speichern fehlgeschlagen.'),
  });

  const normal = Math.max(0, 100 - kora - trocken);

  return (
    <div className="kv-block">
      <div className="ein-hinweis">
        Der Wurf passiert einmal beim Anmelden und gilt für die ganze Sitzung – nicht je Ansicht.
        Sonst spräche Kora auf einer Seite sachlich und auf der nächsten frech.
      </div>

      <div className="kv-balken" aria-hidden="true">
        <span className="kv-balken-teil normal" style={{ width: `${normal}%` }} />
        <span className="kv-balken-teil kora" style={{ width: `${kora}%` }} />
        <span className="kv-balken-teil trocken" style={{ width: `${trocken}%` }} />
      </div>
      <div className="kv-balken-legende">
        <span>Normal {normal} %</span>
        <span>Kora {kora} %</span>
        <span>Trocken {trocken} %</span>
      </div>

      {([
        ['Kora', kora, setKora, 'anteilKora'],
        ['Trocken', trocken, setTrocken, 'anteilTrocken'],
      ] as const).map(([label, wert, setzen, feld]) => (
        <label className="kv-regler" key={feld}>
          <span>{label}</span>
          <input
            type="range"
            min={0}
            max={60}
            step={5}
            value={wert}
            disabled={e.nurNormal}
            onChange={(ev) => setzen(Number(ev.target.value))}
            onMouseUp={() => speichern.mutate({ [feld]: wert })}
            onTouchEnd={() => speichern.mutate({ [feld]: wert })}
            onKeyUp={() => speichern.mutate({ [feld]: wert })}
          />
          <span className="kv-reglerwert">{wert} %</span>
        </label>
      ))}

      {([
        ['nurNormal', 'Nur Normal', 'Überschreibt die Regler – Kora spricht dann ausschließlich sachlich.'],
        ['kollegenKommentare', 'Kollegen-Kommentare im Team-Überblick', 'Nur Mengen, nie Leistung.'],
        ['vornamen', 'Sätze mit Vornamen', 'Die Anrede in „Meine Aufgaben".'],
      ] as const).map(([feld, label, hinweis]) => (
        <div className="ein-schalterzeile" key={feld}>
          <div>
            {label}
            <div className="ein-hinweis">{hinweis}</div>
          </div>
          <button
            type="button"
            className={`ein-schalter ${e[feld] ? 'an' : ''}`}
            onClick={() => speichern.mutate({ [feld]: !e[feld] })}
          >
            <span />
          </button>
        </div>
      ))}

      <Vorschau />
    </div>
  );
}

/**
 * Wie Kora klingt – ein Satz, neu gewürfelt.
 *
 * Nimmt bewusst die echte Lage des Angemeldeten und keine erfundenen Zahlen:
 * Eine Vorschau mit Fantasiewerten sagt nichts darüber, wie der Satz morgen
 * früh in der eigenen Maske steht.
 */
function Vorschau() {
  const abfrage = useQuery({
    queryKey: ['kora-vorschau'],
    queryFn: () => api.hole<KoraSatz | null>('/kora/vorschau'),
    /* Ein Wurf gilt, bis jemand würfelt – nicht bis React neu rendert. */
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  return (
    <div className="kv-vorschau">
      <div className="kv-vorschau-kopf">
        <span className="ein-titel">Vorschau</span>
        <button
          type="button"
          className="kv-wuerfeln"
          onClick={() => void abfrage.refetch()}
          disabled={abfrage.isFetching}
        >
          Neu würfeln
        </button>
      </div>
      {abfrage.data ? (
        <KoraPille satz={abfrage.data} />
      ) : (
        <div className="ein-hinweis">
          Zurzeit trifft keine Regel zu – Kora hätte gerade nichts zu sagen. Gewürfelt wird nur
          unter den Sätzen, die auf die heutige Lage passen; erfundene Zahlen zeigt die Vorschau
          nicht.
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Schwellwerte
// ---------------------------------------------------------------------------

function Schwellen({
  stand,
  onGeaendert,
  melden,
}: {
  stand: KoraVerwaltungStand;
  onGeaendert: () => void;
  melden: (t: string) => void;
}) {
  const s = stand.schwellen;

  const speichern = useMutation({
    mutationFn: (daten: Record<string, number>) => api.aendere('/kora/schwellen', daten),
    onSuccess: onGeaendert,
    onError: (f) => melden(f instanceof ApiFehler ? f.message : 'Speichern fehlgeschlagen.'),
  });

  const felder: { feld: keyof typeof s; label: string; einheit: string }[] = [
    { feld: 'warnTage', label: 'Warnstufe', einheit: 'Tage' },
    { feld: 'kritTage', label: 'Kritisch', einheit: 'Tage' },
    { feld: 'geldEur', label: 'Viel Geld offen', einheit: '€' },
    { feld: 'offenN', label: 'Sehr viel offen', einheit: 'Vorgänge' },
    { feld: 'datenalterTage', label: 'Datenalter', einheit: 'Tage' },
  ];

  return (
    <div className="kv-block">
      {/*
        Der wichtigste Satz dieses Reiters: Es gibt keine zweite Stelle, an der
        „14 Tage" steht. Wer hier dreht, färbt auch die Liste und den Ring.
      */}
      <div className="kv-warnung">
        Warnstufe und kritisch färben zugleich Liste und Ring in „Meine Aufgaben" – es sind
        dieselben Werte, nicht zwei gleich benannte.
      </div>

      {felder.map((f) => (
        <label className="ein-feldzeile" key={f.feld}>
          <span>{f.label}</span>
          <span className="ein-eingabezeile">
            <input
              type="number"
              className="ein-katfeld ein-zahl"
              defaultValue={s[f.feld]}
              min={0}
              onBlur={(e) => {
                const w = Number(e.target.value) || 0;
                if (w !== s[f.feld]) speichern.mutate({ [f.feld]: w });
              }}
            />
            <span className="ein-einheit">{f.einheit}</span>
          </span>
        </label>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Portraits
// ---------------------------------------------------------------------------

function Portraits({
  stand,
  onGeaendert,
  melden,
}: {
  stand: KoraVerwaltungStand;
  onGeaendert: () => void;
  melden: (t: string) => void;
}) {
  const [laeuft, setLaeuft] = useState('');

  async function hochladen(pose: string, datei: File) {
    setLaeuft(pose);
    try {
      const formular = new FormData();
      formular.append('datei', datei);
      await api.datei(`/kora/portrait/${pose}`, formular);
      melden(`Portrait für ${pose} ersetzt.`);
      onGeaendert();
    } catch (f) {
      melden(f instanceof ApiFehler ? f.message : 'Der Upload ist fehlgeschlagen.');
    } finally {
      setLaeuft('');
    }
  }

  return (
    <div className="kv-block">
      <div className="ein-hinweis">
        Angenommen werden PNG-Dateien mit genau 400 × 400 Pixeln. Ersetzen überschreibt; die vorige
        Fassung bleibt dreißig Tage erhalten. Fehlt ein Portrait, nutzt Kora Neutral – nie einen
        leeren Kreis.
      </div>

      <div className="kv-portraits">
        {stand.portraits.map((p) => (
          <div className="kv-portrait" key={p.pose}>
            <span className="kv-portraitbild">
              <img src={p.bild} alt={`Portrait ${p.pose}`} />
            </span>
            <div className="kv-portraittext">
              <div className="kv-portraitname">{p.pose}</div>
              <div className="ein-hinweis">{p.regeln.join(' · ') || 'Keiner Regel zugeordnet'}</div>
              {/*
                Kein Bernstein für den Normalfall. Die Anleitung sah hier
                „fehlt – Standardportrait aktiv" in Warnfarbe vor; es fehlt
                aber nichts: Alle fünf Portraits sind mitgeliefert und
                vollständig. Fünf dauerhafte Warnungen, die niemand abstellen
                kann, gewöhnen einem nur das Hinsehen ab.
              */}
              <div className="kv-portraitstand">
                {p.eigen
                  ? `hochgeladen am ${dat(p.hochgeladenAm.slice(0, 10))}`
                  : 'mitgeliefertes Portrait aktiv'}
              </div>
              <label className="kv-ersetzen">
                {laeuft === p.pose ? 'Wird übertragen …' : 'Ersetzen'}
                <input
                  type="file"
                  accept="image/png"
                  hidden
                  disabled={laeuft !== ''}
                  onChange={(e) => {
                    const datei = e.target.files?.[0];
                    e.target.value = '';
                    if (datei) void hochladen(p.pose, datei);
                  }}
                />
              </label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
